import React, { useState, useEffect } from 'react';
import { useAuth } from '../../features/auth/hooks/useAuth';
import Button from '../../components/ui/Button';
import ProductForm from '../../components/admin/ProductForm';
import { patternService } from '../../services/patternService';
import { Plus, Edit2, Trash2, ExternalLink, Lock, AlertCircle, RefreshCw, Copy } from 'lucide-react';
import SignInForm from '../../features/auth/components/SignInForm';
import { clearPatternDraft, getPatternDraftKey } from '../../utils/patternDraft';
import { getPatternSaveError } from '../../utils/patternErrors';
import { preparePatternDuplicate } from '../../utils/patternDuplicate';
import { getDifficultyLevel } from '../../constants/patternOptions';

export default function AdminPatterns() {
  const { user } = useAuth();
  const [patterns, setPatterns] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);
  const [duplicatingPattern, setDuplicatingPattern] = useState(null);
  const [duplicateSourceId, setDuplicateSourceId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [saveProgress, setSaveProgress] = useState(null);
  const activeDraftKey = getPatternDraftKey(
    editingPattern?.id || (duplicateSourceId ? `duplicate-${duplicateSourceId}` : null),
  );

  useEffect(() => {
    fetchPatterns();
  }, []);

  const fetchPatterns = async () => {
    try {
      const data = await patternService.getPatterns();
      setPatterns(data);
    } catch (err) {
      setError({
        title: 'Patterns could not be loaded',
        message: 'Refresh the page or try again in a moment.',
        technicalMessage: err.message || 'Unknown error',
        canRetry: false,
      });
      console.error('Supabase Error:', err);
    }
  };

  const savePattern = async (formData) => {
    setIsLoading(true);
    setError(null);
    setSaveProgress({ percent: 0, message: 'Preparing save…' });

    try {
      if (editingPattern) {
        await patternService.createPattern(
          { ...formData, id: editingPattern.id },
          setSaveProgress,
        );
      } else {
        await patternService.createPattern(formData, setSaveProgress);
      }

      clearPatternDraft(
        typeof window !== 'undefined' ? window.localStorage : null,
        activeDraftKey,
      );
      setShowForm(false);
      setEditingPattern(null);
      setDuplicatingPattern(null);
      setDuplicateSourceId(null);
      setLastSubmission(null);
      await fetchPatterns();
    } catch (err) {
      setError({ ...getPatternSaveError(err), canRetry: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    setLastSubmission(formData);
    await savePattern(formData);
  };

  const handleRetry = async () => {
    if (lastSubmission) await savePattern(lastSubmission);
  };

  const openNewPattern = () => {
    setError(null);
    setSaveProgress(null);
    setEditingPattern(null);
    setDuplicatingPattern(null);
    setDuplicateSourceId(null);
    setShowForm(true);
  };

  const openPatternEditor = (pattern) => {
    setError(null);
    setSaveProgress(null);
    setEditingPattern(pattern);
    setDuplicatingPattern(null);
    setDuplicateSourceId(null);
    setShowForm(true);
  };

  const openPatternDuplicate = (pattern) => {
    setError(null);
    setSaveProgress(null);
    setEditingPattern(null);
    setDuplicatingPattern(preparePatternDuplicate(pattern, patterns.map(item => item.slug)));
    setDuplicateSourceId(pattern.id);
    setShowForm(true);
  };

  const closePatternForm = () => {
    setShowForm(false);
    setEditingPattern(null);
    setDuplicatingPattern(null);
    setDuplicateSourceId(null);
    setLastSubmission(null);
  };

  const handleDelete = async (pattern) => {
    if (window.confirm('¿Are you sure you want to delete this pattern?')) {
      try {
        await patternService.deletePattern(pattern.id, pattern.pdf_url, pattern.images, pattern.pdf_files);
        fetchPatterns();
      } catch {
        setError({
          title: 'The pattern could not be deleted',
          message: 'Please try again.',
          canRetry: false,
        });
      }
    }
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-10 rounded-2xl shadow-xl shadow-gray-100 border border-gray-100 text-center">
          <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="text-amber-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold mb-2 text-gray-900">Admin Access Required</h1>
          <p className="text-gray-500 mb-8">Please sign in with your administrator credentials to manage shop patterns.</p>
          <div className="text-left bg-gray-50/50 p-6 rounded-xl border border-gray-50">
            <SignInForm />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-primary">Manage Patterns</h1>
          <p className="text-gray-600">Upload and manage your shop products</p>
        </div>
        {!showForm && (
          <Button
            onClick={openNewPattern}
            className="flex items-center gap-2"
          >
            <Plus size={20} /> New Pattern
          </Button>
        )}
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-red-900" role="alert">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 shrink-0 text-red-600" size={20} />
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{error.title}</p>
              <p className="mt-1 text-sm text-red-700">{error.message}</p>
              {error.technicalMessage && (
                <details className="mt-2 text-xs text-red-500">
                  <summary className="cursor-pointer">Technical details</summary>
                  <p className="mt-1 break-words">{error.technicalMessage}</p>
                </details>
              )}
            </div>
            {error.canRetry && lastSubmission && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleRetry}
                disabled={isLoading}
                className="flex shrink-0 items-center gap-2"
              >
                <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
                Retry
              </Button>
            )}
          </div>
        </div>
      )}

      {showForm ? (
        <div className="animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              {editingPattern ? 'Edit Pattern' : duplicatingPattern ? 'Duplicate Pattern' : 'Add New Pattern'}
            </h2>
          </div>
          <ProductForm
            initialData={editingPattern || duplicatingPattern}
            onSubmit={handleSubmit}
            onCancel={closePatternForm}
            isLoading={isLoading}
            draftKey={activeDraftKey}
            saveProgress={saveProgress}
          />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          {patterns.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">No patterns added yet</p>
              <Button
                onClick={openNewPattern}
                variant="secondary"
              >
                Add your first pattern
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-bottom border-gray-100">
                    <th className="px-6 py-4 font-semibold text-gray-700">Product</th>
                    <th className="px-6 py-4 font-semibold text-gray-700">Level</th>
                    <th className="px-6 py-4 font-semibold text-gray-700">Status</th>
                    <th className="px-6 py-4 font-semibold text-gray-700">Price</th>
                    <th className="px-6 py-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {patterns.map((pattern) => (
                    <tr key={pattern.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <img
                              src={pattern.main_image || pattern.images?.[0]}
                              alt={pattern.title}
                              className="w-12 h-12 rounded object-cover border border-gray-200"
                            />
                            {pattern.status === 'draft' && (
                              <span className="absolute -top-1 -left-1 bg-amber-100 text-amber-700 text-[8px] font-bold px-1 rounded border border-amber-200">
                                DRAFT
                              </span>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{pattern.title}</div>
                            <div className="text-xs text-gray-500">
                              {pattern.slug}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {'★'.repeat(getDifficultyLevel(pattern.difficulty_level || pattern.skill_level).stars)}{' '}
                        {getDifficultyLevel(pattern.difficulty_level || pattern.skill_level).shortLabel}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${pattern.status === 'published'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-amber-50 text-amber-700'
                          }`}>
                          {pattern.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {pattern.price}€
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            className="p-2 text-gray-400 hover:text-primary transition-colors"
                            title="Edit"
                            onClick={() => openPatternEditor(pattern)}
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            className="p-2 text-gray-400 hover:text-primary transition-colors"
                            title="Duplicate"
                            aria-label={`Duplicate ${pattern.title}`}
                            onClick={() => openPatternDuplicate(pattern)}
                          >
                            <Copy size={18} />
                          </button>
                          <button
                            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                            title="Delete"
                            onClick={() => handleDelete(pattern)}
                          >
                            <Trash2 size={18} />
                          </button>
                          {pattern.pdf_files && pattern.pdf_files.length > 0 ? (
                            pattern.pdf_files.map((pdf, idx) => (
                              <span
                                key={idx}
                                className="p-2 text-emerald-600 flex flex-col items-center"
                                title={`Secure PDF (${pdf.language})`}
                              >
                                <ExternalLink size={14} />
                                <span className="text-[8px] font-bold uppercase">{pdf.language.substring(0, 2)}</span>
                              </span>
                            ))
                          ) : pattern.pdf_url && (
                            <a
                              href={pattern.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 text-gray-400 hover:text-green-500 transition-colors"
                              title="View PDF (Legacy)"
                            >
                              <ExternalLink size={18} />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
